import React, { useEffect, useState } from 'react';
import DataTable from '../../components/tables/DataTable';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import StatCard from '../../components/charts/StatCard';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import teamLeadService from '../../services/teamLeadService';

const ManagerTeamLeadsView = () => {
  const [teamLeads, setTeamLeads] = useState([]);
  const [filteredTeamLeads, setFilteredTeamLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const { toasts, removeToast, error } = useToast();

  useEffect(() => {
    loadTeamLeads();
  }, []);

  useEffect(() => {
    filterTeamLeads();
  }, [searchTerm, statusFilter, teamLeads]);

  const loadTeamLeads = async () => {
    setLoading(true);
    const result = await teamLeadService.getAllTeamLeads();
    if (result.success) {
      setTeamLeads(result.data);
      setFilteredTeamLeads(result.data);
    } else {
      error(result.error || 'Failed to load Team Leads');
    }
    setLoading(false);
  };

  const filterTeamLeads = () => {
    let filtered = [...teamLeads];

    if (searchTerm) {
      filtered = filtered.filter(tl =>
        tl.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tl.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(tl => tl.status === statusFilter);
    }

    setFilteredTeamLeads(filtered);
  };

  const getTeamLeadStats = () => {
    const total = teamLeads.length;
    const active = teamLeads.filter(tl => tl.is_active === true).length;
    return { total, active };
  };

  const stats = getTeamLeadStats();

  const filterBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'flex-end'
  };

  const filterGroupStyle = {
    flex: '1',
    minWidth: '200px'
  };

  const selectStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--surface)',
    color: 'var(--text-primary)',
    outline: 'none',
    fontFamily: 'inherit'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return '#10b981';
      case 'Available': return '#3b82f6';
      case 'Busy': return '#f59e0b';
      case 'On Leave': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Team Lead',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'var(--primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: '700',
            fontSize: '16px'
          }}>
            {value.charAt(0)}
          </div>
          <div>
            <div style={{ fontWeight: '600' }}>{value}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {row.email}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'contact',
      label: 'Contact'
    },
    {
      key: 'location',
      label: 'Location / Site',
      render: (value, row) => {
        const firstSite = (Array.isArray(row.sites) && row.sites.length > 0)
          ? row.sites[0]
          : row.site;
        const locationDisplay = firstSite?.location || 'No Location';
        const siteNames = Array.isArray(row.sites) && row.sites.length > 0
          ? row.sites.map((site) => site.name || site.site_name).filter(Boolean)
          : row.site?.name || row.site_name
            ? [row.site?.name || row.site_name]
            : [];
        const siteDisplay = siteNames.length > 0 ? siteNames.length===1 ? siteNames[0] : siteNames.length +' Sites' : 'No Sites Assigned';
        return (
          <div>
            <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{locationDisplay}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }} title={siteDisplay}>{siteDisplay}</div>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${getStatusColor(value)}20`,
          color: getStatusColor(value)
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'engineers_count',
      label: 'Engineers',
      render: (value) => (
        <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
          {value || 0}
        </span>
      )
    },
    {
      key: 'active_tickets',
      label: 'Active Tickets',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '700',
          background: 'var(--surface-light)',
          color: 'var(--text-primary)'
        }}>
          {value || 0}
        </span>
      )
    },
    {
      key: 'completed_this_month',
      label: 'Completed',
      render: (value) => `${value || 0} tickets`
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading team leads...
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <div style={gridStyle}>
        <StatCard
          title="Total Team Leads"
          value={stats.total}
          icon=""
          color="#3b82f6"
        />
        <StatCard
          title="Active Today"
          value={stats.active}
          icon=""
          subtitle="Currently working"
          color="#10b981"
        />
      </div>

      <div style={filterBarStyle}>
        <div style={filterGroupStyle}>
          <Input
            placeholder="Search team leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            }
          />
        </div>
        <div style={filterGroupStyle}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
            Filter by Status
          </label>
          <select
            style={selectStyle}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">All Status</option>
            <option value="Active">Active</option>
            <option value="Busy">Busy</option>
            <option value="On Leave">On Leave</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <Button variant="secondary" onClick={loadTeamLeads}>
             Refresh
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredTeamLeads}
        actions={[]}
        pageSize={15}
      />
    </div>
  );
};

export default ManagerTeamLeadsView;
